import { useEffect, useState } from "react";
import { Ship } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { useAuth } from "../../context/AuthContext";
import { useYacht } from "../../context/YachtContext";
import { useTour } from "./TourContext";

const SENIOR_ROLES = ["owner", "manager", "captain"];

/**
 * One-time prompt for brand-new senior users (no yachts yet) offering the
 * interactive "add a yacht" tour. Dismissal is remembered per-user in
 * localStorage so it never nags twice.
 */
export default function WelcomeTourPrompt() {
  const { user } = useAuth();
  const { yachts, loading } = useYacht();
  const { startTour, activeTour } = useTour();
  const [open, setOpen] = useState(false);

  const key = user ? `seavanto_tour_add_yacht_prompted_${user.id}` : null;

  useEffect(() => {
    if (!user || loading || activeTour) return;
    if (!SENIOR_ROLES.includes((user.role || "").toLowerCase())) return;
    if (user.account_kind === "provider") return;
    if ((yachts || []).length > 0) return;
    if (localStorage.getItem(key)) return;
    const t = setTimeout(() => setOpen(true), 900);
    return () => clearTimeout(t);
  }, [user, loading, yachts, activeTour, key]);

  const dismiss = () => {
    if (key) localStorage.setItem(key, "1");
    setOpen(false);
  };
  const start = () => {
    dismiss();
    startTour("add-yacht");
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) dismiss(); }}>
      <DialogContent className="max-w-md" data-testid="welcome-tour-prompt">
        <DialogHeader>
          <div className="inline-grid place-items-center h-12 w-12 rounded-full bg-zinc-50 border border-zinc-200 mb-1">
            <Ship className="h-6 w-6 text-zinc-600" />
          </div>
          <DialogTitle className="font-display">Welcome aboard Seavanto</DialogTitle>
          <DialogDescription>
            Everything starts with a yacht. Want a quick interactive tour that walks you
            through adding your first one? It takes about a minute.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={dismiss} data-testid="welcome-tour-skip">
            Maybe later
          </Button>
          <Button className="bg-ink hover:bg-black" onClick={start} data-testid="welcome-tour-start">
            Start the tour
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
